'use client'
import { useState } from 'react'
import { Container } from '@/components/ui'

// Import individual feature components
import HyperAI from './Homepage/hyper-ai'
import AIChatFeature from './Homepage/ai-chat-feature'
import TaskWriter from './Homepage/task-writer'
import Inbox from './inbox'
import Dictation from './Homepage/dictation'
import DictateAndImprove from './Homepage/dictate-and-improve'

import { GradientVariant, HeadlineVariant } from '@/types/ui'

interface TabItem {
  id: string
  label: string
  content: React.ReactNode
  gradientVariant?: GradientVariant
  headlineVariant?: HeadlineVariant
}

interface FeaturesTabbedSectionProps {
  headlineVariant?: HeadlineVariant
}

const FeaturesTabbedSection = ({ headlineVariant = "regular-mobile" }: FeaturesTabbedSectionProps) => {
  const [activeTab, setActiveTab] = useState('ai-assistant')

  const tabs: TabItem[] = [
    {
      id: 'ai-assistant',
      label: 'HyperAI',
      gradientVariant: '12',
      headlineVariant,
      content: <HyperAI gradientVariant='' headlineVariant={headlineVariant} />
    },
    {
      id: 'task-writer',
      label: 'AI Task Writer',
      gradientVariant: '12',
      headlineVariant,
      content: <TaskWriter gradientVariant='' headlineVariant={headlineVariant} />
    },
    {
      id: 'smart-inbox',
      label: 'Split Inbox',
      gradientVariant: '11',
      headlineVariant,
      content: <Inbox gradientVariant='' headlineVariant={headlineVariant} />
    },
    {
      id: 'dictation',
      label: 'Dictation',
      gradientVariant: '12',
      headlineVariant,
      content: <Dictation gradientVariant='' headlineVariant={headlineVariant} />
    },
    {
      id: 'dictate-improve',
      label: 'Dictate & Improve',
      gradientVariant: '13',
      headlineVariant,
      content: <DictateAndImprove gradientVariant='' headlineVariant={headlineVariant} />
    },
    {
      id: 'ai-chat',
      label: 'AI Chat',
      gradientVariant: '12',
      headlineVariant,
      content: <AIChatFeature className="" gradientVariant='' headlineVariant={headlineVariant} />
    },



  ]

  return (
    <>
      <>
        {/* On mobile, render content without Container; on sm+ use Container */}
        <div 
        style={{
          background: `
                  radial-gradient(35% 40% at 30% 50%, rgb(63 15 135 / 32%) 0%, rgb(93 2 229 / 10%) 40%, rgba(0, 0, 0, 0) 100%), radial-gradient(60% 40% at 80% 30%, rgb(123 35 255 / 18%) 0%, rgb(34 14 70) 40%, rgba(0, 0, 0, 0) 100%), linear-gradient(334deg, rgb(11 6 33 / 92%) 0%, rgba(30, 22, 54, 0.85) 60%, rgba(0, 0, 0, 0) 100%)
                `
        }}
          className="block sm:hidden">
          <div className="backdrop-blur-sm rounded-lg min-w-full bg-black/10 border-white/10 border-opacity-40 p-0 shadow-2xl">
            {/* Tab Navigation */}
            <div
              className="flex overflow-x-auto no-scrollbar gap-1 border-b border-white/10 pb-4 px-1"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="flex flex-nowrap justify-center min-w-max w-full">
                {tabs.map((tab, index) => (
                  <div key={tab.id} className="flex items-center">
                    <button
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-3 py-2 text-sm font-medium transition-all duration-300 whitespace-nowrap relative group border-b-2 ${activeTab === tab.id
                          ? 'text-white border-white'
                          : 'text-white/70 hover:text-white rounded-md hover:bg-white/5 border-transparent'
                        }`}
                    >
                      {tab.label}
                    </button>
                    {index < tabs.length - 1 && (
                      <div className="w-1 h-1 bg-white/20 rounded-full mx-2"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {/* Tab Content */}
            <div className="relative">
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  className={`transition-opacity duration-300 ${activeTab === tab.id
                      ? 'opacity-100 relative'
                      : 'opacity-0 absolute inset-0 pointer-events-none'
                    }`}
                >
                  {tab.content}
                </div>
              ))}
            </div>
          </div>
        </div>


        <Container variant="section" className="hidden sm:block">
          <div className="backdrop-blur-sm rounded-lg min-w-full sm:border border-white/10 border-opacity-40 p-0 md:p-6 shadow-2xl relative overflow-hidden">
            {/* Left Gradient Background */}
            {/* Blended center radial gradient background, inspired by Superhuman's hero section */}
            <div
              className="absolute inset-0 pointer-events-none z-0"
              aria-hidden="true"
              style={{
                background: `
                  radial-gradient(35% 40% at 30% 50%, rgb(63 15 135 / 32%) 0%, rgb(93 2 229 / 10%) 40%, rgba(0, 0, 0, 0) 100%), radial-gradient(60% 40% at 80% 30%, rgb(123 35 255 / 18%) 0%, rgb(34 14 70) 40%, rgba(0, 0, 0, 0) 100%), linear-gradient(334deg, rgb(11 6 33 / 92%) 0%, rgba(30, 22, 54, 0.85) 60%, rgba(0, 0, 0, 0) 100%)
                `
              }}
            />
            {/* Tab Navigation */}
            <div
              className="flex overflow-x-auto no-scrollbar gap-1 md:gap-2 border-b border-white/10 pb-4 px-1 md:px-0 relative"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="flex flex-nowrap justify-center min-w-max w-full">
                {tabs.map((tab, index) => (
                  <div key={tab.id} className="flex items-center">
                    <button
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-3 py-2 text-sm md:text-base font-medium transition-all duration-300 whitespace-nowrap relative group border-b-2 ${activeTab === tab.id
                          ? 'text-white border-white'
                          : 'text-white/70 hover:text-white rounded-md hover:bg-white/5 border-transparent'
                        }`}
                    >
                      {tab.label}
                    </button>
                    {index < tabs.length - 1 && (
                      <div className="w-1 h-1 bg-white/20 rounded-full mx-2"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {/* Tab Content */}
            <div className="relative">
              {tabs.map((tab) => (
                <div
                  key={tab.id}
                  className={`transition-opacity duration-300 ${activeTab === tab.id
                      ? 'opacity-100 relative'
                      : 'opacity-0 absolute inset-0 pointer-events-none'
                    }`}
                >
                  {tab.content}
                </div>
              ))}
            </div>
          </div>
        </Container>
      </>
      {/* Tabbed Interface Container */}
    </>
  )
}

export default FeaturesTabbedSection
